import React, { useState, useMemo } from "react";
import { Property } from "../types";
import { computeDealScore, bandLabel, bandColor } from "../lib/dealScore";

interface DealScoreProps {
  property: Property;
  /**
   * "chip" — compact pill for property cards (small score badge with band label).
   * "badge" — square score box (number + "SCORE") for the redesigned property card.
   * "inline" — bare number + "SCORE" caption for the dense list row (editorial blue/gray).
   * "full" — large score with expandable factor breakdown for the property modal.
   */
  variant?: "chip" | "badge" | "inline" | "full";
}

const DealScore: React.FC<DealScoreProps> = ({ property, variant = "chip" }) => {
  const result = useMemo(() => computeDealScore(property), [property]);
  const [open, setOpen] = useState(false);

  const color = bandColor(result.band);

  if (variant === "chip") {
    return (
      <div
        className="deal-score-chip"
        style={{
          color,
          borderColor: `${color}66`,
          backgroundColor: `${color}15`,
        }}
        title={`${bandLabel(result.band)} (${result.score}/100). Click the property to see the factor breakdown.`}
      >
        <span className="deal-score-chip-num">{result.score}</span>
        <span className="deal-score-chip-band">{bandLabel(result.band)}</span>
      </div>
    );
  }

  if (variant === "badge") {
    // Square score box for the redesigned property card (number + "SCORE").
    return (
      <div
        className="deal-score-badge"
        style={{ color }}
        title={`${bandLabel(result.band)} (${result.score}/100). Open the property for the factor breakdown.`}
      >
        <span className="deal-score-badge-num">{result.score}</span>
        <span className="deal-score-badge-label">SCORE</span>
      </div>
    );
  }

  if (variant === "inline") {
    // Bare number + "SCORE" caption for the dense list row. Editorial palette:
    // brand blue for a strong score (>=70), muted gray otherwise — deliberately
    // NOT the green/amber band colors (the redesign keeps the score on-brand).
    const inlineColor = result.score >= 70 ? "var(--accent-blue)" : "var(--text-secondary)";
    return (
      <div
        className="deal-score-inline"
        title={`${bandLabel(result.band)} (${result.score}/100). Open the property for the factor breakdown.`}
      >
        <span className="deal-score-inline-num" style={{ color: inlineColor }}>{result.score}</span>
        <span className="deal-score-inline-label">SCORE</span>
      </div>
    );
  }

  // "full" variant: big score with expandable breakdown.
  return (
    <div className="deal-score-full" style={{ borderColor: `${color}66` }}>
      <button
        type="button"
        className="deal-score-full__header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="deal-score-full__num-block">
          <div className="deal-score-full__num" style={{ color }}>
            {result.score}
          </div>
          <div className="deal-score-full__num-suffix">/100</div>
        </div>
        <div className="deal-score-full__label-block">
          <div className="deal-score-full__band" style={{ color }}>
            {bandLabel(result.band)}
          </div>
          <div className="deal-score-full__hint">
            {open ? "Hide factor breakdown" : "Show factor breakdown"}
          </div>
        </div>
        <span className="deal-score-full__chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div className="deal-score-full__factors">
          {result.factors.map((f) => {
            const pct = f.weight > 0 ? Math.round((f.weighted / f.weight) * 100) : 0;
            return (
              <div key={f.label} className="deal-score-factor">
                <div className="deal-score-factor__row">
                  <span className="deal-score-factor__label">{f.label}</span>
                  <span className="deal-score-factor__weight">
                    {Math.round(f.weighted)} / {f.weight}
                  </span>
                </div>
                <div className="deal-score-factor__bar-track">
                  <div
                    className="deal-score-factor__bar-fill"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
                <div className="deal-score-factor__reason">{f.reason}</div>
              </div>
            );
          })}
          <div className="deal-score-full__formula" title="Heuristic Phase 1 — see lib/dealScore.ts">
            Score = 40 × Equity + 25 × Actionability + 20 × Urgency + 15 × Data Sanity
            <br />
            (Heuristic v1 — feedback welcome.)
          </div>
        </div>
      )}
    </div>
  );
};

export default DealScore;
